import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { Search, ChevronLeft, ChevronRight, Loader2, RotateCcw, Download, X, ArrowRightLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { fetchPayments, refundPayment, moveRefundToCredit } from '../services/adminService'
import { exportToCsv } from '../utils/exportCsv'

type RefundDestination = 'CREDIT' | 'GATEWAY'

interface Payment {
  id: string
  amount: number
  currency: string
  status: string
  method: string
  reference: string
  type?: 'SUBSCRIPTION' | 'PICKUP' | 'TOPUP' | 'DONATION'
  discountApplied?: boolean
  // Part of `amount` that came off the resident's credit at booking; the gateway only charged the rest.
  creditApplied?: number
  refundDestination?: RefundDestination | null
  refundGatewayStatus?: 'PENDING' | 'PROCESSED' | 'FAILED' | 'NEEDS_ATTENTION' | null
  refundedAt?: string | null
  createdAt: string
  user: { id: string; name: string; email: string; phone: string | null } | null
}

// What POST /payments/:id/refund reports once the money has moved.
interface RefundResult {
  credited: number
  gatewayAmount: number
  pickupCancelled: boolean
  residentCreditBalance: number
  /** Open pickups booked under a refunded plan, cancelled with it. */
  includedPickupsCancelled?: number
  /** True when this payment paid for the current period and the plan ended with it. */
  subscriptionEnded?: boolean
}

// What POST /payments/:id/refund/to-credit reports once a stuck gateway refund
// has been put on the resident's credit instead.
interface MoveToCreditResult {
  converted: boolean
  amount: number
  residentCreditBalance: number
}

const TYPE_TABS = [
  { value: 'ALL', label: 'All Types' },
  { value: 'SUBSCRIPTION', label: 'Subscription' },
  { value: 'PICKUP', label: 'Pickup' },
  { value: 'TOPUP', label: 'Top-up' },
  { value: 'DONATION', label: 'Donation' },
]

const TYPE_STYLES: Record<string, { bg: string; text: string }> = {
  SUBSCRIPTION: { bg: 'bg-blue-100', text: 'text-blue-700' },
  PICKUP: { bg: 'bg-green-100', text: 'text-green-700' },
  TOPUP: { bg: 'bg-gray-100', text: 'text-gray-600' },
  DONATION: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
}

const STATUS_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'REFUNDED', label: 'Refunded' },
]

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  FAILED: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  REFUNDED: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
}

const METHOD_LABELS: Record<string, string> = {
  MTN_MOMO: 'MTN MoMo',
  TELECEL_CASH: 'Telecel Cash',
  AIRTELTIGO_MONEY: 'AirtelTigo Money',
  CARD: 'Card',
  PAYSTACK: 'Paystack',
  CREDIT: 'App credit',
}

// How a refund's destination reads next to the REFUNDED badge.
const REFUND_DESTINATION_LABELS: Record<RefundDestination, string> = {
  CREDIT: 'credit',
  GATEWAY: 'Paystack',
}

const PAGE_SIZE = 10
const REASON_MAX_LENGTH = 500

const ghs = (amount: number) =>
  `GHS ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// Only what Paystack actually charged is Paystack's to return; the credit part
// of a payment goes back to credit whatever happens.
const gatewayPortion = (payment: Payment) => payment.amount - (payment.creditApplied ?? 0)

// Axios puts the backend's { message } under response.data; nothing else about
// the error is useful to the admin.
const asApiError = (err: unknown) => err as { response?: { status?: number; data?: { message?: string } } }

interface DialogProps {
  title: string
  subtitle: string
  onClose: () => void
  closeDisabled: boolean
  children: ReactNode
}

// The shell both money dialogs share: a dimmed backdrop, a centred card and a
// header whose close button is held shut while a request is in flight.
function Dialog({ title, subtitle, onClose, closeDisabled, children }: DialogProps) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payments-dialog-title"
        className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 id="payments-dialog-title" className="text-lg font-bold text-[#1A1A2E]">{title}</h3>
            <p className="text-xs text-[#6B7280]">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            className="p-1 rounded-lg hover:bg-gray-100 text-[#6B7280] disabled:opacity-50"
            title="Close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  )
}

function DetailList({ children }: { children: ReactNode }) {
  return (
    <dl className="bg-[#F8FAF9] border border-gray-100 rounded-xl px-4 py-3 text-xs space-y-1.5">{children}</dl>
  )
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[#6B7280]">{label}</dt>
      <dd className="text-right font-medium text-[#1A1A2E]">{children}</dd>
    </div>
  )
}

function ResidentRow({ payment }: { payment: Payment }) {
  return (
    <DetailRow label="Resident">
      {payment.user?.name ?? '—'}
      {payment.user?.email && (
        <span className="block text-[10px] font-normal text-[#6B7280]">{payment.user.email}</span>
      )}
    </DetailRow>
  )
}

function ReferenceRow({ payment }: { payment: Payment }) {
  return (
    <DetailRow label="Reference">
      <span className="font-mono text-primary-700 break-all">{payment.reference ?? payment.id}</span>
    </DetailRow>
  )
}

interface ReasonInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

function ReasonInput({ id, value, onChange, disabled }: ReasonInputProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-[#6B7280] mb-1">
        Reason (shown in the audit log)
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        maxLength={REASON_MAX_LENGTH}
        placeholder="Optional"
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
      />
    </div>
  )
}

export default function Payments() {
  const [payments, setPayments] = useState<Payment[]>([])
  const [total, setTotal] = useState(0)
  const [totalRevenue, setTotalRevenue] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  // Sent to the server a moment after typing stops, so it searches every payment.
  const [serverSearch, setServerSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => {
      setServerSearch(search.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [search])
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [page, setPage] = useState(1)

  const loadPayments = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchPayments({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        type: typeFilter !== 'ALL' ? typeFilter : undefined,
        search: serverSearch || undefined,
        page,
        limit: PAGE_SIZE,
      } as Parameters<typeof fetchPayments>[0])
      setPayments(data?.payments ?? [])
      setTotal(data?.pagination?.total ?? 0)
      setTotalRevenue(data?.totalRevenue ?? 0)
    } catch {
      toast.error('Failed to load payments')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, typeFilter, page, serverSearch])

  useEffect(() => { loadPayments() }, [loadPayments])

  const [refundingId, setRefundingId] = useState<string | null>(null)
  // The payment the refund dialog is open for, and the admin's choices inside it.
  const [refundTarget, setRefundTarget] = useState<Payment | null>(null)
  const [destination, setDestination] = useState<RefundDestination>('CREDIT')
  const [reason, setReason] = useState('')

  // The gateway refund being moved onto credit, and the note that goes with it.
  const [movingId, setMovingId] = useState<string | null>(null)
  const [moveTarget, setMoveTarget] = useState<Payment | null>(null)
  const [moveReason, setMoveReason] = useState('')

  const submitting = refundTarget !== null && refundingId === refundTarget.id
  const creditApplied = refundTarget?.creditApplied ?? 0
  const gatewayUnavailable =
    refundTarget === null || refundTarget.method === 'CREDIT' || gatewayPortion(refundTarget) <= 0

  const moving = moveTarget !== null && movingId === moveTarget.id
  const moveAmount = moveTarget ? gatewayPortion(moveTarget) : 0

  const openRefund = (payment: Payment) => {
    setDestination('CREDIT')
    setReason('')
    setRefundTarget(payment)
  }

  const closeRefund = useCallback(() => {
    if (refundingId !== null) return
    setRefundTarget(null)
  }, [refundingId])

  const openMove = (payment: Payment) => {
    setMoveReason('')
    setMoveTarget(payment)
  }

  const closeMove = useCallback(() => {
    if (movingId !== null) return
    setMoveTarget(null)
  }, [movingId])

  // Escape closes whichever dialog is open, like its X button does, but never mid-request.
  useEffect(() => {
    if (!refundTarget && !moveTarget) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      closeRefund()
      closeMove()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [refundTarget, moveTarget, closeRefund, closeMove])

  const submitRefund = async () => {
    if (!refundTarget) return
    setRefundingId(refundTarget.id)
    try {
      const result: RefundResult = await refundPayment(refundTarget.id, {
        destination,
        reason: reason.trim() || undefined,
      })
      const name = refundTarget.user?.name ?? 'the resident'
      const credited = Number(result?.credited ?? 0)
      const gatewayAmount = Number(result?.gatewayAmount ?? 0)
      const parts: string[] = []
      if (credited > 0) parts.push(`${ghs(credited)} added to ${name}'s credit`)
      if (gatewayAmount > 0) parts.push(`${ghs(gatewayAmount)} refund queued at Paystack`)
      if (result?.pickupCancelled) parts.push('pickup cancelled')
      const included = Number(result?.includedPickupsCancelled ?? 0)
      if (result?.subscriptionEnded) parts.push('subscription ended')
      if (included > 0) parts.push(included === 1 ? '1 included pickup cancelled' : `${included} included pickups cancelled`)
      toast.success(parts.length ? parts.join(' · ') : 'Payment refunded', { duration: 6000 })
      setRefundTarget(null)
      loadPayments()
    } catch (err) {
      // Paystack's own refusal text is the useful part: it tells the admin
      // whether to try again or send the money to credit instead, so the
      // dialog stays open with their choices intact.
      toast.error(asApiError(err).response?.data?.message ?? 'Failed to process refund')
    } finally {
      setRefundingId(null)
    }
  }

  const submitMove = async () => {
    if (!moveTarget) return
    setMovingId(moveTarget.id)
    try {
      const result: MoveToCreditResult = await moveRefundToCredit(moveTarget.id, {
        reason: moveReason.trim() || undefined,
      })
      const name = moveTarget.user?.name ?? 'the resident'
      toast.success(`${ghs(Number(result?.amount ?? moveAmount))} moved to ${name}'s credit`, { duration: 6000 })
      setMoveTarget(null)
      loadPayments()
    } catch (err) {
      const { response } = asApiError(err)
      toast.error(response?.data?.message ?? 'Failed to move the refund to credit')
      // 409 means this row is stale — the refund was already moved, or the
      // webhook settled it — so refresh rather than leave a button that cannot work.
      if (response?.status === 409) {
        setMoveTarget(null)
        loadPayments()
      }
    } finally {
      setMovingId(null)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const filtered = payments.filter((p) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      p.id.toLowerCase().includes(q) ||
      p.reference?.toLowerCase().includes(q) ||
      (p.user?.name?.toLowerCase().includes(q) ?? false) ||
      (p.user?.email?.toLowerCase().includes(q) ?? false)
    )
  })

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Revenue', value: `GHS ${totalRevenue.toLocaleString()}`, color: 'text-green-700 bg-green-50' },
          { label: 'Total Payments', value: total, color: 'text-blue-600 bg-blue-50' },
          { label: 'Page', value: `${page} / ${totalPages || 1}`, color: 'text-yellow-700 bg-yellow-50' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-[#6B7280] font-medium">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color.split(' ')[0]}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
        <div className="flex items-center gap-2 bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2">
          <Search className="w-4 h-4 text-[#6B7280]" />
          <input
            type="text"
            placeholder="Search by ID, reference, name, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => { setStatusFilter(value); setPage(1) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                statusFilter === value
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-[#F8FAF9] text-[#6B7280] hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
          <span className="w-px h-6 bg-gray-200 mx-1 self-center" />
          {TYPE_TABS.map(({ value, label }) => (
            <button
              key={`type-${value}`}
              onClick={() => { setTypeFilter(value); setPage(1) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                typeFilter === value
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-[#F8FAF9] text-[#6B7280] hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1A1A2E]">
            {total} payment{total !== 1 ? 's' : ''}
          </p>
          <button
            type="button"
            onClick={() => {
              if (!filtered.length) return
              exportToCsv(
                ['ID', 'User', 'Email', 'Amount', 'Currency', 'Method', 'Type', 'Status', 'Reference', 'Date'],
                filtered.map((p: Payment) => [
                  p.id,
                  p.user?.name ?? '',
                  p.user?.email ?? '',
                  String(p.amount),
                  p.currency ?? 'GHS',
                  p.method ?? '',
                  p.type ?? '',
                  p.status,
                  p.reference ?? '',
                  new Date(p.createdAt).toLocaleDateString(),
                ]),
                'payments'
              )
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Reference', 'User', 'Amount', 'Type', 'Method', 'Date', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((payment) => {
                  const st = STATUS_STYLES[payment.status] ?? STATUS_STYLES.PENDING
                  return (
                    <tr key={payment.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono font-semibold text-primary-700">
                        {payment.reference?.slice(0, 12) ?? payment.id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-[#1A1A2E]">{payment.user?.name ?? '—'}</p>
                        <p className="text-[10px] text-[#6B7280]">{payment.user?.email ?? ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold text-[#1A1A2E]">
                          GHS {payment.amount?.toLocaleString() ?? 0}
                        </span>
                        {payment.discountApplied && (
                          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700">17% off</span>
                        )}
                        {(payment.creditApplied ?? 0) > 0 && payment.status !== 'REFUNDED' && (
                          <p className="text-[10px] text-[#6B7280]">{ghs(payment.creditApplied ?? 0)} from credit</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {payment.type ? (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_STYLES[payment.type]?.bg ?? 'bg-gray-100'} ${TYPE_STYLES[payment.type]?.text ?? 'text-gray-600'}`}>
                            {payment.type}
                          </span>
                        ) : (
                          <span className="text-xs text-[#6B7280]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">
                        {METHOD_LABELS[payment.method] ?? payment.method}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(payment.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${st.bg} ${st.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                          {payment.status}
                        </span>
                        {payment.status === 'REFUNDED' && payment.refundDestination && (
                          <p
                            className="text-[10px] text-[#6B7280] mt-0.5 whitespace-nowrap"
                            title={payment.refundedAt ? `Refunded ${new Date(payment.refundedAt).toLocaleString()}` : undefined}
                          >
                            → {REFUND_DESTINATION_LABELS[payment.refundDestination]}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {payment.status === 'COMPLETED' && (
                          <button
                            onClick={() => openRefund(payment)}
                            disabled={refundingId === payment.id}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                          >
                            {refundingId === payment.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <RotateCcw size={12} />
                            )}
                            Refund
                          </button>
                        )}
                        {payment.status === 'REFUNDED' && payment.refundDestination === 'GATEWAY' &&
                          payment.refundGatewayStatus !== 'PROCESSED' && payment.refundGatewayStatus !== 'PENDING' && (
                          <button
                            onClick={() => openMove(payment)}
                            disabled={movingId === payment.id}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 disabled:opacity-50 transition-colors whitespace-nowrap"
                            title="Paystack could not return this; put it on the resident's credit instead"
                          >
                            {movingId === payment.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <ArrowRightLeft size={12} />
                            )}
                            Move to credit
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-[#6B7280]">
                      No payments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                    p === page ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Refund dialog */}
      {refundTarget && (
        <Dialog title="Refund payment" subtitle="This cannot be undone." onClose={closeRefund} closeDisabled={submitting}>
          <DetailList>
            <ResidentRow payment={refundTarget} />
            <DetailRow label="Amount">
              <span className="font-bold">{ghs(refundTarget.amount)}</span>
            </DetailRow>
            <DetailRow label="Method">{METHOD_LABELS[refundTarget.method] ?? refundTarget.method}</DetailRow>
            <ReferenceRow payment={refundTarget} />
            {creditApplied > 0 && (
              <div className="pt-1.5 border-t border-gray-200 text-[11px] text-[#6B7280]">
                {ghs(creditApplied)} of this was paid from credit and goes back to credit either way.
              </div>
            )}
          </DetailList>

          <fieldset>
            <legend className="text-xs font-semibold text-[#6B7280] mb-2">Where should the money go?</legend>
            <div className="space-y-2">
              <label
                className={`flex items-start gap-3 border rounded-xl px-3 py-2.5 cursor-pointer transition-colors ${
                  destination === 'CREDIT' ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="refund-destination"
                  value="CREDIT"
                  checked={destination === 'CREDIT'}
                  onChange={() => setDestination('CREDIT')}
                  disabled={submitting}
                  autoFocus
                  className="mt-0.5 accent-primary-600"
                />
                <span>
                  <span className="block text-sm font-semibold text-[#1A1A2E]">Add to the resident's SmartWasteCycle credit</span>
                  <span className="block text-[11px] text-[#6B7280]">Instant. Applied automatically to their next pickup.</span>
                </span>
              </label>
              <label
                className={`flex items-start gap-3 border rounded-xl px-3 py-2.5 transition-colors ${
                  gatewayUnavailable
                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-70'
                    : destination === 'GATEWAY'
                      ? 'border-primary-600 bg-primary-50 cursor-pointer'
                      : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                }`}
              >
                <input
                  type="radio"
                  name="refund-destination"
                  value="GATEWAY"
                  checked={destination === 'GATEWAY'}
                  onChange={() => setDestination('GATEWAY')}
                  disabled={submitting || gatewayUnavailable}
                  className="mt-0.5 accent-primary-600"
                />
                <span>
                  <span className="block text-sm font-semibold text-[#1A1A2E]">Return to the original payment method via Paystack</span>
                  <span className="block text-[11px] text-[#6B7280]">
                    Can take a few days. Mobile money refunds depend on the provider; if Paystack cannot process it you will be alerted.
                  </span>
                  {gatewayUnavailable && (
                    <span className="block text-[11px] font-semibold text-amber-700 mt-1">
                      Not available: nothing was charged to a payment method, so there is no gateway money to return.
                    </span>
                  )}
                </span>
              </label>
            </div>
          </fieldset>

          <ReasonInput id="refund-reason" value={reason} onChange={setReason} disabled={submitting} />

          {refundTarget.type === 'PICKUP' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-[11px] text-amber-800">
              If the pickup this paid for has not started, it will be cancelled.
            </div>
          )}
          {refundTarget.type === 'SUBSCRIPTION' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-[11px] text-amber-800">
              If this payment pays for the current period, refunding it ends the resident's subscription now. The open included pickups booked in the period it paid for are cancelled.
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={closeRefund}
              disabled={submitting}
              className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitRefund}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-red-600 rounded-xl text-sm font-semibold text-white hover:bg-red-700 transition disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Refund {ghs(refundTarget.amount)}
            </button>
          </div>
        </Dialog>
      )}

      {/* Move-to-credit dialog */}
      {moveTarget && (
        <Dialog title="Move refund to credit" subtitle="This cannot be undone." onClose={closeMove} closeDisabled={moving}>
          <DetailList>
            <ResidentRow payment={moveTarget} />
            <DetailRow label="Moves to credit">
              <span className="font-bold">{ghs(moveAmount)}</span>
            </DetailRow>
            <ReferenceRow payment={moveTarget} />
          </DetailList>

          <p className="text-xs text-[#1A1A2E]">
            Only for a refund Paystack reported as failed or needing attention: the resident sees it as SmartWasteCycle credit at once. If Paystack later returns it anyway, admins are emailed to recover the credit.
          </p>

          <ReasonInput id="move-reason" value={moveReason} onChange={setMoveReason} disabled={moving} />

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={closeMove}
              disabled={moving}
              className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitMove}
              disabled={moving}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-primary-600 rounded-xl text-sm font-semibold text-white hover:bg-primary-700 transition disabled:opacity-50"
            >
              {moving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />}
              Move {ghs(moveAmount)} to credit
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}
